import React from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { pricingPackages } from '../data/mock';

const PricingSection = () => {
  const scrollToContact = () => {
    document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
  };

  const getColorClass = (color) => {
    const colors = {
      'mid-purple': 'border-white/[0.08] hover:border-[#d987ff]/30',
      'light-pink': 'border-[#ff84e4]/20 hover:border-[#ff84e4]/40',
      'mid-blue': 'border-white/[0.08] hover:border-[#88a2ff]/30',
    };
    return colors[color] || colors['mid-purple'];
  };

  const getAccentColor = (color) => {
    const colors = {
      'mid-purple': '#d987ff',
      'light-pink': '#ff84e4',
      'mid-blue': '#88a2ff',
    };
    return colors[color] || colors['mid-purple'];
  };

  return (
    <section id="pricing" data-testid="pricing-section" className="py-12 lg:py-20 relative">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] lg:w-[600px] h-[400px] lg:h-[600px] bg-[#ff84e4]/3 rounded-full blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-10 lg:mb-16">
          <span className="inline-block px-4 py-1.5 bg-white/[0.04] border border-white/[0.08] rounded-full text-sm text-gray-400 mb-4 lg:mb-5 tracking-wide">
            Zusammenarbeit
          </span>
          <h2 className="text-2xl lg:text-4xl font-bold mb-4 lg:mb-5 text-white">
            Modelle für unterschiedliche
            <span className="text-gradient"> Projektgrößen</span>
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-sm lg:text-base leading-relaxed">
            Ob Testprojekt, Kampagnenproduktion oder laufende Zusammenarbeit – wir 
            strukturieren jedes Projekt passend zu Umfang, Ziel und Content-Bedarf.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-4 lg:gap-6">
          {pricingPackages.map((pkg) => (
            <div
              key={pkg.id}
              data-testid={`pricing-card-${pkg.id}`}
              className={`relative p-6 lg:p-8 rounded-3xl bg-white/[0.02] border transition-colors duration-200 ${getColorClass(pkg.color)} ${
                pkg.highlighted ? 'ring-1 ring-[#ff84e4]/20' : ''
              }`}
            >
              {/* Package Name */}
              <h3
                className="text-xl font-bold mb-2"
                style={{ color: getAccentColor(pkg.color) }}
              >
                {pkg.name}
              </h3>
              <p className="text-gray-500 text-sm mb-8 leading-relaxed">
                {pkg.subtitle}
              </p>

              {/* Features */}
              <ul className="space-y-3.5 mb-8">
                {pkg.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start gap-3">
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0"
                      style={{ backgroundColor: `${getAccentColor(pkg.color)}15` }}
                    >
                      <Check size={11} style={{ color: getAccentColor(pkg.color) }} />
                    </div>
                    <span className="text-gray-400 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <button
                data-testid={`pricing-cta-${pkg.id}`}
                onClick={scrollToContact}
                className={`w-full py-3.5 rounded-full font-medium text-sm flex items-center justify-center gap-2 transition-all duration-300 ${
                  pkg.highlighted
                    ? 'bg-[#ff84e4] text-black hover:bg-[#ffd1e7]'
                    : 'bg-white/[0.06] text-gray-300 hover:bg-white/[0.1] border border-white/[0.08]'
                }`}
              >
                {pkg.cta}
                <ArrowRight size={16} />
              </button>
            </div>
          ))}
        </div>

        {/* Bottom Note */}
        <p className="text-center text-gray-600 text-sm mt-10 max-w-xl mx-auto leading-relaxed">
          Jedes Projekt wird individuell kalkuliert – abhängig von Anzahl der Visuals, 
          Komplexität, Stilrichtung und Retouching-Aufwand.
        </p>
      </div>
    </section>
  );
};

export default PricingSection;
